import { useCallback, useEffect, useState } from "react";

import { useToast } from "~/hooks/use-toast";
import {
  createSalesCondition,
  deleteSalesCondition,
  fetchSalesConditions,
  updateSalesCondition,
} from "~/services/salesConditionService";
import { SalesCondition } from "~/types/client";

export const useSalesConditions = () => {
  const [salesConditions, setSalesConditions] = useState<SalesCondition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Fetch sales conditions
  const fetchConditions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const conditions = await fetchSalesConditions();
      setSalesConditions(conditions);
    } catch (err: any) {
      setError(err.message || "Failed to fetch sales conditions");
      toast({
        title: "Error",
        description: "No se pudieron cargar las condiciones de venta.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Create a new sales condition
  const addSalesCondition = useCallback(
    async (
      condition: Omit<SalesCondition, "id" | "createdAt" | "updatedAt">
    ) => {
      try {
        const newCondition = await createSalesCondition(condition);
        if (newCondition) {
          setSalesConditions((prev) => [...prev, newCondition]);
          toast({
            title: "Éxito",
            description: "Condición de venta creada correctamente.",
          });
          return newCondition;
        }
        throw new Error("Failed to create sales condition");
      } catch (err: any) {
        toast({
          title: "Error",
          description: err?.message || "No se pudo crear la condición de venta.",
          variant: "destructive",
        });
        return null;
      }
    },
    [toast]
  );

  // Update an existing sales condition
  const updateCondition = useCallback(
    async (
      id: string,
      updates: Partial<Omit<SalesCondition, "id" | "createdAt" | "updatedAt">>
    ) => {
      try {
        const updatedCondition = await updateSalesCondition(id, updates);
        if (updatedCondition) {
          setSalesConditions((prev) =>
            prev.map((condition) =>
              condition.id === id ? updatedCondition : condition
            )
          );
          toast({
            title: "Éxito",
            description: "Condición de venta actualizada correctamente.",
          });
          return updatedCondition;
        }
        throw new Error("Failed to update sales condition");
      } catch (err: any) {
        toast({
          title: "Error",
          description: "No se pudo actualizar la condición de venta.",
          variant: "destructive",
        });
        return null;
      }
    },
    [toast]
  );

  // Delete a sales condition
  const removeCondition = useCallback(
    async (id: string) => {
      try {
        const success = await deleteSalesCondition(id);
        if (success) {
          setSalesConditions((prev) =>
            prev.filter((condition) => condition.id !== id)
          );
          toast({
            title: "Éxito",
            description: "Condición de venta eliminada correctamente.",
          });
          return true;
        }
        throw new Error("Failed to delete sales condition");
      } catch (err: any) {
        toast({
          title: "Error",
          description: "No se pudo eliminar la condición de venta.",
          variant: "destructive",
        });
        return false;
      }
    },
    [toast]
  );

  // Get a sales condition by ID
  const getConditionById = useCallback(
    (id: string | undefined) => {
      if (!id) return null;
      return salesConditions.find((condition) => condition.id === id) || null;
    },
    [salesConditions]
  );

  // Load sales conditions on component mount
  useEffect(() => {
    fetchConditions();
  }, [fetchConditions]);

  return {
    salesConditions,
    loading,
    error,
    fetchConditions,
    addSalesCondition,
    updateCondition,
    removeCondition,
    getConditionById,
  };
};
